import { Button } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import { Link } from 'gatsby';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, Scene } from 'react-scrollmagic';

import { CONTACT_US_PAGE } from '../../links';
import ClockIcon from './icons/clock';
import CalendarIcon from './icons/schedule';
import TagIcon from './icons/tag';
import SectionTitle from './section-title';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(10, 0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(23, 0)
    }
  },
  animate: {
    position: 'relative',
    top: 20,
    transition:
      'opacity 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955), top 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955)',
    opacity: 0
  },
  show: {
    top: 0,
    opacity: 1
  },
  title: {
    marginBottom: theme.spacing(14),
    transitionDelay: '0.1s'
  },
  wrapContent: {
    padding: theme.spacing(6),
    boxShadow: '0px 8px 32px #BDD7F0',
    transitionDelay: '0.4s',
    '& a': {
      textDecoration: 'none'
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8)
    }
  },
  itemBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(14),
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'center',
      marginBottom: theme.spacing(6)
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      '&>div': {
        padding: theme.spacing(4, 0, 3.5)
      }
    }
  },
  subTitle: {
    marginBottom: theme.spacing(4),
    lineHeight: 1
  },
  divider: {
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    },
    [theme.breakpoints.up('sm')]: {
      margin: '0 auto'
    }
  },
  icon: {
    marginBottom: theme.spacing(6),
    fontSize: 52,
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(6),
      marginBottom: 0
    }
  },
  clockBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(6, 4),
    backgroundColor: theme.palette.grey[300],
    border: `1px solid ${theme.colors.borderColor}`,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      padding: theme.spacing(8)
    }
  },
  iconClock: {
    marginBottom: theme.spacing(6),
    fontSize: 92,
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(9),
      marginBottom: 0
    }
  },
  subTitleClock: {
    marginTop: theme.spacing(6),
    lineHeight: 1,
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(2)
    }
  },
  link: {
    marginTop: theme.spacing(14),
    [theme.breakpoints.up('md')]: {
      marginTop: 0
    },
    [theme.breakpoints.up('lg')]: {
      '& button': {
        minWidth: 200
      }
    }
  },
  contactLink: {
    color: 'black'
  }
}));

const PricingContent = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));
  const middleMatches = useMediaQuery(theme.breakpoints.up('md'));
  
  return (
    <section className={classes.root}>
      <Container maxWidth="xl">
        <Controller>
          <Scene
            classToggle={[`.${classes.animate}`, `${classes.show}`]}
            triggerElement="#whiteLabelPricing"
            indicators={false}
            reverse={false}
            offset="-200px"
          >
            <div id="whiteLabelPricing">
              <SectionTitle
                className={clsx(classes.title, classes.animate)}
                title={t('whiteLabelPage.pricing.title')}
                align="center"
              />
              <Paper className={clsx(classes.wrapContent, classes.animate)}>
                <Grid container>
                  <Grid className={classes.itemBlock} item xs={12} sm={3}>
                    <TagIcon className={classes.icon} />
                    <div>
                      <Link to={CONTACT_US_PAGE} className={classes.contactLink}>
                        <Typography
                          className={classes.subTitle}
                          variant="h4"
                          align={middleMatches ? 'left' : 'center'}
                        >
                          Contact Sales
                        </Typography>
                      </Link>
                      <Typography
                        variant="body2"
                        align={middleMatches ? 'left' : 'center'}
                      >
                        {t('whiteLabelPage.pricing.priceInformation')}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid className={classes.itemBlock} item xs={12} sm={4}>
                    <CalendarIcon className={classes.icon} />
                    <div>
                      <Typography
                        className={classes.subTitle}
                        variant="h4"
                        align={middleMatches ? 'left' : 'center'}
                      >
                        {t('whiteLabelPage.pricing.date')}
                      </Typography>
                      <Typography
                        variant="body2"
                        align={middleMatches ? 'left' : 'center'}
                      >
                        {t('whiteLabelPage.pricing.deliveryTime')}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid className={classes.itemBlock} item xs={12} sm={1}>
                    <Divider
                      className={classes.divider}
                      orientation={matches ? 'vertical' : 'horizontal'}
                    />
                  </Grid>
                  <Grid className={classes.itemBlock} item xs={12} sm={4}>
                    <Typography
                      variant="body2"
                      align={middleMatches ? 'left' : 'center'}
                    >
                      {t('whiteLabelPage.pricing.dateDescr')}
                    </Typography>
                  </Grid>
                  <Grid className={classes.clockBlock} item xs={12}>
                    <Box
                      display="flex"
                      flexDirection={middleMatches ? 'row' : 'column'}
                      alignItems="center"
                      width="100%"
                    >
                      <ClockIcon className={classes.iconClock} />
                      <div>
                        <Typography
                          variant="body1"
                          align={middleMatches ? 'left' : 'center'}
                        >
                          {t('whiteLabelPage.pricing.estimatedDate')}
                        </Typography>
                      </div>
                    </Box>
                    <Link className={classes.link} to={CONTACT_US_PAGE}>
                      <Button variant="contained" color="primary">
                        {t('whiteLabelPage.pricing.button')}
                      </Button>
                    </Link>
                  </Grid>
                </Grid>
              </Paper>
            </div>
          </Scene>
        </Controller>
      </Container>
    </section>
  );
};

export default PricingContent;
